import { Component, EventEmitter, OnInit, Output, Signal, computed, effect } from '@angular/core';
import { SelectModule } from 'primeng/select';
import { UploadFileComponent } from '../../dialogue/upload-file/upload-file.component';
import { FileSystemService } from '../../../services/file-system.service';
import { Node } from '../../explorer/node';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastService } from '../../../services/toast.service';
import { Repo, RepoService } from '../../../services/repo.service';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { OauthService } from '../../../services/oauth.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { phosphorPlusCircle, phosphorXCircle } from '@ng-icons/phosphor-icons/regular';
import { FloatLabelModule } from 'primeng/floatlabel';
import { Usdm2Service } from '../../../services/ai/usdm2.service';

type newRepoSelectionState =
  | {
      type: 'local';
      state:
        | 'url_invalid'
        | 'repo_exists'
        | 'repo_does_not_exist'
        | 'repo_created'
        | 'repo_creation_failed'
        | 'repo_id_mismatch';
    }
  | {
      type: 'cloud';
      state:
        | 'repo_selected'
        | 'new_repo_request'
        | 'repo_created'
        | 'repo_creation_failed'
        | 'repo_exists';
    };

@Component({
  selector: 'app-study-creation',
  imports: [
    SelectModule,
    UploadFileComponent,
    MultiSelectModule,
    TreeSelectModule,
    FormsModule,
    NgIconComponent,
    FloatLabelModule,
  ],
  templateUrl: './study-creation.component.html',
  styleUrl: './study-creation.component.css',
  providers: [provideIcons({ phosphorPlusCircle, phosphorXCircle })],
})
export class StudyCreationComponent implements OnInit {
  repo: Signal<Repo | null> = computed(() => this.repoService.repo());
  rootNode: Node | null = null;
  isConnecting: boolean = false;
  isCloudExistingConnecting: boolean = false;
  repoSelectionState: newRepoSelectionState | null = null;
  fileTree: TreeNode[] = [];
  usdmSelectedNode: TreeNode | null = null;
  osbServerUrl: string = environment.osburl;
  cloudRepos: Repo[] = [];
  newCloudRepoName: string = '';
  localUrl: string = '';
  publicOsbStudyId: string = '';
  environment = environment;

  @Output() studyCreationFinished = new EventEmitter<void>();

  constructor(
    private fs: FileSystemService,
    private toast: ToastService,
    private repoService: RepoService,
    private oauth: OauthService,
    private usdm: Usdm2Service,
    private http: HttpClient
  ) {
    effect(() => {
      if (this.usdm.usdmSession()) {
        this.publicOsbStudyId = this.usdm.usdmSession()?.public_osb_study_id || '';
      }
    })
  }

  ngOnInit() {
    this.fs.rootNodeRefresh$.subscribe(node => {
      this.fileTree = this.getFileTree(node);
      this.rootNode = node;
    });

    if (this.currentStudy) {
      this.fs.refreshRootNode();
    }

    this.repoService.getRepos().then((repos: Repo[]) => {
      this.cloudRepos = repos.filter(repo => repo.type.toLowerCase() === 'cloud');
    });

    this.usdmSelectedNode = this.usdm.protocol ? {
      label: this.usdm.protocol.name,
      data: this.usdm.protocol
    } : null;

    this.publicOsbStudyId = this.usdm.publicOsbStudyId || '';
  }

  getFileTree(node: Node): TreeNode[] {
    return node.items.map(item => ({
      key: item.path,
      label: item.name,
      data: item,
      selectable: !item.isFolder,
      children: item.items.length > 0 ? this.getFileTree(item) : [],
    }));
  }

  get currentStudy(): string | null {
    return this.repoService.repo()?.name || null;
  }

  setRepo(value: Repo | null) {
    this.repoService.setRepo(value);
  }

  async connectCloudExistingRepo(repoName: string, repoId: string) {
    // console.log('Connecting to existing cloud repository:', repoName, repoId);
    this.isCloudExistingConnecting = true;
    // this.startLoadingMessages();
    try {
      const repo_Id = String(repoId);
      const cloudStatus = await this.repoService.getCloudRepoStatus(repo_Id);
      const repoServerUrl =
        environment.userMcpMicroserviceUrl +
        '/' +
        this.oauth.user?.user_id +
        '-' +
        repo_Id;
      if (cloudStatus.returncode !== '0') {
        await this.repoService.startCloudRepo(repo_Id);
      } else {
        const repo_Id = await this.repoService.getIdFromRepo(repoServerUrl);
        if (!repo_Id) {
          await this.repoService.createAssignRepo(
            repoName,
            'cloud',
            repoServerUrl
          );
        }
      }
      const repoDetails = await this.repoService.fetchRepoDetails(
        repo_Id,
        repoServerUrl
      );
      this.repoService.setRepo(repoDetails);
      this.toast.showSuccess('Connected to repository successfully.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.repoSelectionState = {
        type: 'cloud',
        state: 'repo_creation_failed',
      };
      if (error.cause?.errorType === 'REPO_CREATION_FAILED') {
        console.error('Study creation failed:', error.cause);
        throw new Error('Failed to create study. Please try again.', {
          cause: error.cause,
        });
      } else if (error.cause?.errorType === 'REPO_ASSIGNMENT_FAILED') {
        console.error('Study assignment failed:', error.cause);
        throw new Error('Failed to assign study. Please try again.', {
          cause: error.cause,
        });
      } else {
        console.error('Error connecting to existing cloud repository:', error);
        throw new Error(
          'Failed to connect to existing cloud repository. Please try again.',
          {
            cause: error.cause,
          }
        );
      }
    } finally {
      this.isCloudExistingConnecting = false;
      setTimeout(() => {
        this.fs.refreshRootNode();
      }, 1000);
      // this.stopLoadingMessages();
    }
  }

  async newRepo(repoName: string, type: 'local' | 'cloud', serverUrl: string) {
    this.isConnecting = true;
    // this.startLoadingMessages();
    if (!repoName) {
      this.toast.showError('Study name cannot be empty.');
      return;
    }
    try {
      await this.repoService.createAssignRepo(repoName, type, serverUrl);
      this.toast.showSuccess('Study created and assigned successfully.');
      this.repoSelectionState = {
        type: type,
        state: 'repo_created',
      };
      sessionStorage.setItem('currentRepoUrl', serverUrl);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Update repo selection state at the end of catch block
      this.repoSelectionState = {
        type: type,
        state: 'repo_creation_failed',
      };
      if (error.cause?.errorType === 'REPO_CREATION_FAILED') {
        console.error('Study creation failed:', error.cause);
        throw new Error('Failed to create study. Please try again.', {
          cause: error.cause,
        });
      } else if (error.cause?.errorType === 'REPO_ASSIGNMENT_FAILED') {
        console.error('Study assignment failed:', error.cause);
        throw new Error('Failed to assign study. Please try again.', {
          cause: error.cause,
        });
      } else {
        console.error('Unexpected error while creating study:', error);
        throw new Error(
          'An unexpected error occurred while creating the study.',
          {
            cause: error.cause,
          }
        );
      }
    } finally {
      this.isConnecting = false;
      setTimeout(() => {
        this.fs.refreshRootNode();
      }, 1000);
      // this.stopLoadingMessages();
    }
  }

  async localSubmitServerUrl(url: string) {
    if (!url) {
      this.repoSelectionState = {
        type: 'local',
        state: 'url_invalid',
      };
    }

    sessionStorage.setItem('currentRepoUrl', url);
    try {
      const repoId = await this.repoService.getIdFromRepo(url);

      if (repoId) {
        const repoDetails = await this.repoService.fetchRepoDetails(
          repoId,
          url
        );
        this.repoService.setRepo(repoDetails);
        if (repoDetails) {
          this.toast.showSuccess('Connected to repository successfully.');
        }
      } else {
        this.repoSelectionState = {
          type: 'local',
          state: 'repo_does_not_exist',
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.cause?.details?.includes('0 rows')) {
        this.repoSelectionState = {
          type: 'local',
          state: 'repo_does_not_exist',
        };
      } else if (error.cause?.name === 'HttpErrorResponse') {
        this.repoSelectionState = {
          type: 'local',
          state: 'url_invalid',
        };
        throw new Error('Invalid URL or server not reachable.', {
          cause: {
            code: 'REPO_NOT_FOUND',
          },
        });
      } else {
        console.error(error.cause);
        throw new Error('Error fetching repo details: ' + error.message, {
          cause: error.cause,
        });
      }
    }

    console.log('Repo selection state:', this.repoSelectionState);
    setTimeout(async () => {
      await this.fs.refreshRootNode();
    }, 1000);
  }

  onFileUpload = (uploadedNode: Node) => {
    const treeNode: TreeNode = {
      label: uploadedNode.name,
      data: uploadedNode
    };
    this.usdmSelectedNode = treeNode;
    this.usdm.protocol = uploadedNode;
    console.log('Uploaded node selected:', this.usdmSelectedNode.data);
  };

  onSubmit() {
    if (this.usdmSelectedNode === null) {
      this.toast.showError('Please select a protocol file before submitting.');
      return;
    }

    if (this.publicOsbStudyId) {
      this.http.get(`${environment.osburl}/api/studies/list?minimal=true`, {}).subscribe((response: any) => {
        for (const item of response) {
          if (item?.id === this.publicOsbStudyId) {
            console.log('Study already exists:', item);
            this.usdm.privateOsbStudyId = item.uid;
            console.log('OSB study ID:', this.usdm.privateOsbStudyId);
            return;
          }
        }
      });
    }

    if (this.osbServerUrl.trim() === '') {
      this.toast.showError('Please enter the OSB Server URL.');
      return;
    } else {
      this.usdm.osbServerUrl = this.osbServerUrl.trim();
      this.usdm.protocol = this.usdmSelectedNode.data as Node;
      this.usdm.publicOsbStudyId = this.publicOsbStudyId;
    }

    this.toast.showSuccess('Protocol file submitted successfully.');
    this.studyCreationFinished.emit();
  }
}
